import { gsap } from 'gsap';
import MorphSVGPlugin from 'gsap/MorphSVGPlugin';
import React, { forwardRef, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Fade, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { DocsIcon, HelpIcon } from '~/elements/icons';
import { Logo } from '~/elements/Logo';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { isAllowedAppAccess } from '~/lib/session';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';
import { log } from '~/util';

import { MenuItem } from './MenuItem';
import { Platform } from './Toolbar';

gsap.registerPlugin(MorphSVGPlugin);

interface SidebarProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export const PLATFORM_ENTRIES = [
  {
    label: 'Settings',
    path: ROUTE.PLATFORM_SETTINGS,
    activePath: ROUTE.PLATFORM_SETTINGS,
  },
  {
    label: 'Team',
    path: ROUTE.PLATFORM_TEAM,
    activePath: ROUTE.PLATFORM_TEAM,
  },
  {
    label: 'Roles',
    path: ROUTE.PLATFORM_ROLES,
    activePath: ROUTE.PLATFORM_ROLES,
  },
  {
    label: 'API Keys',
    path: ROUTE.PLATFORM_API_KEYS,
    activePath: `${ROUTE.PLATFORM_API_KEYS}/edit`,
  },
  {
    label: 'Webhooks',
    path: ROUTE.PLATFORM_WEBHOOKS,
    activePath: `${ROUTE.PLATFORM_WEBHOOKS}/edit`,
  },
];

export const TRANSFER_ENTRIES = [
  {
    label: 'Overview',
    path: ROUTE.TRANSFERS,
    activePath: `${ROUTE.TRANSFERS}/edit`,
  },
  {
    label: 'Batches',
    path: ROUTE.TRANSFER_BATCHES,
    activePath: ROUTE.TRANSFER_BATCHES,
  },
  {
    label: 'Pending Approvals',
    path: ROUTE.TRANSFER_APPROVALS,
    activePath: ROUTE.TRANSFER_APPROVALS,
  },
];

export const LOANS_ENTRIES = [
  {
    label: 'Overview',
    path: ROUTE.LOANS,
    activePath: `${ROUTE.LOANS}/edit`,
  },
  {
    label: 'Disbursements',
    path: `${ROUTE.LOANS}/disbursements`,
    activePath: `${ROUTE.LOANS}/disbursements`,
  },
  {
    label: 'Payments',
    path: `${ROUTE.LOANS}/payments`,
    activePath: `${ROUTE.LOANS}/payments`,
  },
];

export const ENTITIES_ENTRIES = [
  {
    label: 'Overview',
    path: ROUTE.ENTITIES,
    activePath: `${ROUTE.ENTITIES}/edit`,
  },
  {
    label: 'Root Entity',
    path: ROUTE.ENTITIES_ROOT_ENTITY,
    activePath: ROUTE.ENTITIES_ROOT_ENTITY,
  },
];

export const DOCUMENT_ENTRIES = [
  {
    label: 'Statements',
    path: ROUTE.STATEMENTS,
    activePath: ROUTE.STATEMENTS,
  },
  {
    label: 'Reporting',
    path: ROUTE.REPORTING,
    activePath: ROUTE.REPORTING,
  },
];

export const EVENTS = [
  {
    label: 'Overview',
    path: ROUTE.EVENTS,
  },
  {
    label: 'Webhook',
    path: ROUTE.EVENTS_WEBHOOK,
  },
];

const Wrapper = styled.div<{ $isOpen: boolean }>`
  --sidebar-o: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: var(--sidebar-width);
  position: fixed;
  top: 0;
  bottom: -8px;
  left: 0;
  z-index: ${({ $isOpen }) => ($isOpen ? '15' : '5')};
  transition: transform 0.2s ease-in-out;
  overflow: auto;
  box-shadow: ${({ $isOpen }) =>
    $isOpen
      ? `
        1px 0 0 0 rgba(0, 0, 0, 0.065),
        0 2px 16px 0 rgba(0, 0, 0, 0.05)
        `
      : 'none'};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : 'calc(var(--sidebar-width) * -1)')});
  background-color: ${({ theme }) => theme.secondary.blendToBackground(75)};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    box-shadow: none;
    background: transparent;
    transform: translateX(0);
  }
`;

const StyledFade = styled(Fade)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const Nav = styled.div`
  --sidebar-scrollbar-background: transparent;
  --sidebar-thumb-background: transparent;

  display: flex;
  flex-direction: column;
  opacity: var(--sidebar-o);
  margin: 0;
  padding: 12px;
  height: 100%;
  justify-content: space-between;
  scrollbar-width: none;
  scrollbar-color: var(--sidebar-thumb-background) var(--sidebar-scrollbar-background);
  overflow-y: scroll;

  &:hover {
    --sidebar-thumb-background: ${({ theme }) => theme.secondary.blendToBackground(150)};
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: var(--sidebar-scrollbar-background);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--sidebar-thumb-background);
    border-radius: 6px;
    border: 3px solid var(--sidebar-scrollbar-background);
  }
`;

const Section = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  padding-bottom: 8px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;

  &:last-child {
    border: 0;
  }

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      border-bottom: 1px solid ${theme.secondary.blendToBackground(150)};
    `}
`;

const LogoLink = styled.button`
  background: none;
  outline: none;
  border: none;
  display: flex;
  padding: 0;
  align-items: center;
  cursor: pointer;
  min-height: 56px;
`;

const CloseButton = styled.button`
  background: none;
  outline: none;
  border: none;
  display: flex;
  padding: 0;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 10;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 14;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'all' : 'none')};
  transition:
    opacity 0.2s ease-in-out,
    visibility 0s linear ${({ $isOpen }) => ($isOpen ? '0s' : '0.2s')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const LinkItem = styled.a`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.background};
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.1s;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        color: ${theme.secondary.blendToBackground(1300)};
        background-color: ${theme.secondary.blendToBackground(50)};

        svg {
          --icon-color: ${theme.secondary.blendToBackground(1300)};
          --icon-background: ${theme.secondary.blendToBackground(50)};
        }
      `};
  }

  svg {
    --icon-size: 20px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
    --icon-background: ${({ theme }) => theme.secondary.blendToBackground(50)};

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        --icon-background: ${theme.background};
      `};
  }

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      svg {
        --icon-background: ${theme.background};
      }

      &:hover {
        color: ${theme.secondary.blendToBackground(1300)};
        background-color: ${theme.secondary.blendToBackground(50)};

        svg {
          --icon-color: ${theme.secondary.blendToBackground(1300)};
          --icon-background: ${theme.secondary.blendToBackground(50)};
        }
      }
    `};
`;

export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(({ className, isOpen = false, onClose }, ref) => {
  const { currentPlatform, currentUser, mfaStatus } = useSessionStore();
  const { openHelpSidebar } = useHelpSidebarStore();
  const navigate = useNavigate();

  const featureFlagRequireLiveVerification = useFeatureFlag(FeatureFlag.EnableLiveModeMfaRequired);
  const allowAppAccess = useMemo(
    () => isAllowedAppAccess(currentPlatform, mfaStatus, featureFlagRequireLiveVerification),
    [currentPlatform, currentUser, featureFlagRequireLiveVerification, mfaStatus]
  );
  const handleDocsClick = () => {
    log({
      name: 'Documentation clicked',
      context: currentUser,
    });

    window.open('https://column.com/docs');
  };

  const customRolesEnabled = useFeatureFlag(FeatureFlag.DashboardCustomRoles);
  const platformEntries = useMemo(() => {
    if (customRolesEnabled) {
      return PLATFORM_ENTRIES;
    }
    return PLATFORM_ENTRIES.filter((entry) => entry.label !== 'Roles');
  }, [customRolesEnabled]);

  const transferBatchesEnabled = useFeatureFlag(FeatureFlag.EnableTransferBatches);
  const transferEntries = useMemo(() => {
    if (transferBatchesEnabled) {
      return TRANSFER_ENTRIES;
    }
    return TRANSFER_ENTRIES.filter((entry) => entry.path !== ROUTE.TRANSFER_BATCHES);
  }, [transferBatchesEnabled]);

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <Wrapper className={className} ref={ref} $isOpen={isOpen}>
        <Inner px={16} py={0}>
          <Flex justifyContent="space-between" alignItems="center">
            <LogoLink
              onClick={() => {
                navigate(ROUTE.ROOT);
                onClose?.();
              }}
            >
              <Logo variant="word" size="16" />
            </LogoLink>
            {isOpen && (
              <CloseButton onClick={onClose}>
                <Icon.Close />
              </CloseButton>
            )}
          </Flex>
        </Inner>

        <StyledFade show={!!currentUser}>
          <Platform onNavigate={onClose} />

          <Nav>
            <div>
              <Section>
                {allowAppAccess && (
                  <>
                    <MenuItem label="Home" icon={<Icon.AnimationHome />} path={ROUTE.ROOT} exact onClick={onClose} />
                    <MenuItem
                      label="Platform"
                      icon={<Icon.AnimationLayers />}
                      path={ROUTE.PLATFORM_SETTINGS}
                      entries={platformEntries}
                      activePath={ROUTE.PLATFORM_ROOT}
                      onClick={onClose}
                    />
                  </>
                )}
                <MenuItem label="User" icon={<Icon.AnimationUser />} path={ROUTE.PROFILE} onClick={onClose} />
              </Section>
              {allowAppAccess && (
                <>
                  <Section>
                    <MenuItem
                      label="Transfers"
                      icon={<Icon.AnimationArrows />}
                      path={ROUTE.TRANSFERS}
                      entries={transferEntries}
                      onClick={onClose}
                    />
                    <MenuItem
                      label="Loans"
                      icon={<Icon.AnimationHandCoins />}
                      path={ROUTE.LOANS}
                      entries={LOANS_ENTRIES}
                      onClick={onClose}
                    />
                    <MenuItem
                      label="Accounts"
                      icon={<Icon.AnimationWallet />}
                      path={ROUTE.BANK_ACCOUNTS}
                      onClick={onClose}
                    />
                    <MenuItem
                      label="Entities"
                      icon={<Icon.AnimationDocument />}
                      path={ROUTE.ENTITIES}
                      entries={ENTITIES_ENTRIES}
                      onClick={onClose}
                    />
                  </Section>
                  <Section>
                    <MenuItem
                      label="Documents"
                      icon={<Icon.AnimationDocumentEdit />}
                      path={ROUTE.STATEMENTS}
                      activePath={ROUTE.DOCUMENTS}
                      entries={DOCUMENT_ENTRIES}
                      onClick={onClose}
                    />
                    <MenuItem
                      label="Events"
                      icon={<Icon.AnimationCalendar />}
                      path={ROUTE.EVENTS}
                      entries={EVENTS}
                      activePath={ROUTE.EVENTS}
                      onClick={onClose}
                    />
                  </Section>
                  {currentPlatform?.issuerProcessingDashboardEnabled && (
                    <Section>
                      <MenuItem
                        label="Card Programs"
                        icon={<Icon.AnimationWallet />}
                        path={ROUTE.CARD_PROGRAMS}
                        onClick={onClose}
                      />
                      <MenuItem
                        label="Card Accounts"
                        icon={<Icon.AnimationWallet />}
                        path={ROUTE.CARD_ACCOUNTS}
                        onClick={onClose}
                      />
                      <MenuItem label="Cards" icon={<Icon.AnimationWallet />} path={ROUTE.CARDS} onClick={onClose} />
                      <MenuItem
                        label="Card Tokens"
                        icon={<Icon.AnimationWallet />}
                        path={ROUTE.CARD_TOKENS}
                        onClick={onClose}
                      />
                    </Section>
                  )}
                </>
              )}
            </div>

            <div>
              <LinkItem
                onClick={() => {
                  openHelpSidebar('Support');
                  onClose?.();
                }}
              >
                <HelpIcon /> Help
              </LinkItem>
              <LinkItem
                onClick={() => {
                  handleDocsClick();
                  onClose?.();
                }}
              >
                <DocsIcon /> Docs
              </LinkItem>
            </div>
          </Nav>
        </StyledFade>
      </Wrapper>
    </>
  );
});
